import { useLayoutEffect } from 'react';
import { useSetAtom } from 'jotai';
import type { ChatSession } from '../chat/chat-session.js';
import {
  chatSessionAtom,
  applyChatSessionStateAction,
  chatSessionKeyOf,
  DRAFT_SESSION_KEY,
  emptyDraftSessionState,
} from './atoms.js';
import type { ChatSessionPublishedState } from './atoms.js';

export type ApplyChatSessionState = (action: {
  sessionId: string;
  update: Partial<ChatSessionPublishedState>;
}) => void;

// Keyed at write time by the session's own id (sdk ADR-042).
export function bindChatSessionPublisher(
  session: ChatSession,
  apply: ApplyChatSessionState,
): () => void {
  const push = (update: Partial<ChatSessionPublishedState>): void =>
    apply({ sessionId: chatSessionKeyOf(session.sessionId), update });

  push({
    settings: session.settings,
    messages: session.messages,
    state: session.state,
    progressTree: session.progressTree,
    connectionState: session.connectionState,
    queue: session.queue,
    tasks: session.tasks,
    backgroundWork: session.backgroundWork,
    liveLlmCalls: session.liveLlmCalls,
  });

  const unsubs = [
    session.onMessage(() => push({ messages: [...session.messages] })),
    session.onSettings((settings) => push({ settings })),
    session.onStateChange((state) => push({ state })),
    session.onProgressUpdate((tree) => push({ progressTree: tree })),
    session.onConnectionStateChange((cs) => push({ connectionState: cs })),
    session.onQueueChange((q) => push({ queue: q })),
    session.onTasksChange((tasks) => push({ tasks })),
    session.onBackgroundWorkChange((backgroundWork) => push({ backgroundWork })),
    session.onLiveLlmCalls((calls) => push({ liveLlmCalls: calls })),
    session.onError((e) => push({ error: e })),
    session.onPersisted(() =>
      apply({ sessionId: DRAFT_SESSION_KEY, update: emptyDraftSessionState() }),
    ),
  ];

  return () => {
    unsubs.forEach((unsub) => unsub());
    // An unbinding handle that never persisted leaves its published state
    // under the draft key; the NEXT draft must not paint it for the frame
    // before its own first publish.
    if (session.sessionId === undefined) {
      apply({ sessionId: DRAFT_SESSION_KEY, update: emptyDraftSessionState() });
    }
  };
}

export function useChatSession(session: ChatSession | null): void {
  const setSession = useSetAtom(chatSessionAtom);
  const apply = useSetAtom(applyChatSessionStateAction);

  // Layout effect: bound state lands before the binding commit paints (sdk ADR-042).
  useLayoutEffect(() => {
    setSession(session);
    if (!session) return;
    return bindChatSessionPublisher(session, apply);
  }, [session, setSession, apply]);
}
